import { View, Text, styled } from 'tamagui'
import { Hash, Search, Pin, Users, Lock, Clock, MessageSquare } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'
import type { Channel } from '@/types'

const Header = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  gap: 12,
  flexShrink: 0,
})

const ChannelName = styled(Text, {
  fontFamily: '$fontBody',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
})

const TopicDivider = styled(View, {
  width: 1,
  height: 20,
  backgroundColor: '$borderDefault',
})

const TopicText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$sidebarMuted',
  flex: 1,
  overflow: 'hidden',
})

const HeaderActions = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
})

const IconButton = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

interface ChannelHeaderProps {
  channel: Channel
  memberCount?: number
}

export function ChannelHeader({ channel, memberCount }: ChannelHeaderProps) {
  const { toggleSearch, togglePinned, toggleMemberSidebar, toggleThreadList, searchOpen, pinnedOpen, memberSidebarOpen, threadListOpen } = useAppStore()

  return (
    <Header>
      <Hash size={18} color="$color8" />
      <ChannelName>{channel.name}</ChannelName>
      <Lock size={12} color="$accent" />
      <Text fontFamily="$fontMono" fontSize={10} color="$accent" fontWeight="600">E2E</Text>
      {channel.topic && (
        <>
          <TopicDivider />
          <TopicText numberOfLines={1}>{channel.topic}</TopicText>
        </>
      )}
      {channel.slowmode && (
        <View flexDirection="row" alignItems="center" gap={4} paddingHorizontal={6} paddingVertical={2} borderRadius="$borderRadiusSm" backgroundColor="rgba(232, 168, 64, 0.12)">
          <Clock size={12} color="$warning" />
          <Text fontFamily="$fontMono" fontSize={11} color="$warning" fontWeight="600">{channel.slowmode}s slowmode</Text>
        </View>
      )}
      <HeaderActions>
        <IconButton onPress={toggleSearch} backgroundColor={searchOpen ? '$sidebarActive' : 'transparent'}>
          <Search size={16} color={searchOpen ? '$surfaceForeground' : '$sidebarMuted'} />
        </IconButton>
        <IconButton onPress={togglePinned} backgroundColor={pinnedOpen ? '$sidebarActive' : 'transparent'}>
          <Pin size={16} color={pinnedOpen ? '$surfaceForeground' : '$sidebarMuted'} />
        </IconButton>
        <IconButton onPress={toggleThreadList} backgroundColor={threadListOpen ? '$sidebarActive' : 'transparent'}>
          <MessageSquare size={16} color={threadListOpen ? '$surfaceForeground' : '$sidebarMuted'} />
        </IconButton>
        {memberCount && (
          <IconButton onPress={toggleMemberSidebar} backgroundColor={memberSidebarOpen ? '$sidebarActive' : 'transparent'}>
            <Users size={16} color={memberSidebarOpen ? '$surfaceForeground' : '$sidebarMuted'} />
          </IconButton>
        )}
      </HeaderActions>
    </Header>
  )
}
